'use client';

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ComponentProps
} from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import type { MapView as MapViewType } from '@paction-2.0/ui/components/MapView';

const DynamicMapView = dynamic(
  () => import('@paction-2.0/ui/components/MapView').then((m) => m.MapView),
  { ssr: false }
);

import styles from './styles/lazyMapView.module.scss';

const STATIC_MAP_SRC = '/images/maps/canada-map.webp';

// Delay before the interactive Leaflet map auto-activates (without user
// interaction). Keeping this above ~10 s ensures the static placeholder wins
// the LCP race: Lighthouse stops measuring LCP on first user interaction, and
// most users interact (scroll / tap) well before this timer fires.
// When the user hovers/touches/clicks the map we activate immediately.
const AUTO_ACTIVATE_DELAY_MS = 10_000;

type MapViewProps = ComponentProps<typeof MapViewType>;

export type LazyMapViewProps = MapViewProps & {
  placeholderAlt?: string;
  /** When true (default), renders the static placeholder image. Set to false
   *  when an external fallback already provides the visual (e.g. /puppies
   *  mobile where mobileMapFallback covers the map area). */
  showPlaceholder?: boolean;
  /** Mark the placeholder image as the LCP candidate with fetchpriority=high
   *  and a <link rel="preload">. Default true. */
  priorityImage?: boolean;
  /** Server-rendered map image passed from a Server Component — renders
   *  without the hydration gate so the browser counts it as LCP immediately.
   *  When provided, replaces the built-in static placeholder image. */
  mapPlaceholder?: React.ReactNode;
};

export function LazyMapView({
  placeholderAlt = 'Map of Canada showing breeder locations',
  showPlaceholder = true,
  priorityImage = true,
  mapPlaceholder,
  ...mapProps
}: LazyMapViewProps) {
  const [isReady, setIsReady] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const activatedRef = useRef(false);

  const activate = useCallback(() => {
    if (activatedRef.current) return;
    activatedRef.current = true;
    setIsReady(true);
  }, []);

  // Auto-activate after a long delay (fallback for users who never interact).
  useEffect(() => {
    const idleCb =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : (cb: () => void) => window.setTimeout(cb, 50);

    let idleId: number;
    const timerId = window.setTimeout(() => {
      idleId = idleCb(() => activate()) as unknown as number;
    }, AUTO_ACTIVATE_DELAY_MS);

    return () => {
      window.clearTimeout(timerId);
      if (typeof window.cancelIdleCallback === 'function' && idleId != null) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [activate]);

  return (
    <div
      ref={wrapperRef}
      className={styles.wrapper}
      // Activate the interactive map immediately on any user interaction so
      // that the Leaflet tiles do not load during the LCP measurement window.
      onPointerEnter={activate}
      onFocus={activate}
      onTouchStart={activate}
    >
      {mapPlaceholder ??
        (showPlaceholder && (
          <Image
            src={STATIC_MAP_SRC}
            alt={placeholderAlt}
            fill
            sizes="100vw"
            priority={priorityImage}
            fetchPriority={priorityImage ? 'high' : undefined}
            className={styles.placeholder}
          />
        ))}

      {isReady && (
        <div className={styles.interactiveLayer}>
          <DynamicMapView {...mapProps} />
        </div>
      )}
    </div>
  );
}
